'use client';

import { api } from '@/lib/api/api';
import { EditResourceRequest, IResource, ResourceType } from '@gettactic/api';
import { logError } from '@gettactic/helpers';
import { useLoadingToast } from '@gettactic/helpers/src/hooks/toasts/useLoadingToast';
import { useAuthenticated } from '@gettactic/hooks';
import { useOfficeResources } from '@gettactic/hooks';
import { MapBuilder } from '@gettactic/map-builder/src/MapBuilder';
import { MapCanvasValue } from '@gettactic/map-builder/src/components/map/contexts/MapCanvasContext';
import { useMapBuilderMappedResourcesData } from '@gettactic/map-builder/src/hooks/useMapBuilderMappedResourcesData';
import {
  MapCurrentPosition,
  ResourceEditingOptions,
  SpotShape
} from '@gettactic/map-builder/src/types';
import {
  assignResourceIdToSpotId,
  getDeletedSpotsWithResources,
  getSpotNameFromSpotShapeItem,
  getSpotsWithResourceId,
  getSpotsWithoutResourceId
} from '@gettactic/map-builder/src/utils/spots';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { MapEditorState } from '@gettactic/map-builder/src/components/map/contexts/MapEditorContext';

export interface NewMapEditorProps {
  floor: IResource;
  initialPosition?: MapCurrentPosition;
  setEditingResources: (value: ResourceEditingOptions) => void;
  editingResources?: ResourceEditingOptions;
  onSaved?: () => void;
  editorRef?: React.RefObject<MapEditorState | undefined | null>;
}

export const NewMapEditor: React.FC<NewMapEditorProps> = ({
  floor,
  initialPosition,
  setEditingResources,
  editingResources,
  editorRef,
  onSaved
}) => {
  const queryClient = useQueryClient();
  const toast = useLoadingToast({
    loadingMessage: 'Applying changes...'
  });

  const {
    userContext: { currentOffice }
  } = useAuthenticated();

  const officeId = currentOffice?.id || '';

  const { resources: officeResources } = useOfficeResources({
    officeId,
    startDate: null,
    endDate: null,
    filterQuery: '',
    entireOffice: true
  });

  const floorData = useMapBuilderMappedResourcesData(floor, officeResources);

  const onMapSave = async (data: MapCanvasValue) => {
    if (!floor) {
      toast.error('Floor not found');
      return;
    }

    const nodesWithoutResourceId = getSpotsWithoutResourceId(data.shapes);
    const nodeWithResourceId = getSpotsWithResourceId(data.shapes);
    const nodesToDelete = getDeletedSpotsWithResources(
      (floorData?.shapes as SpotShape[]) || [],
      data.shapes
    );

    if (nodesWithoutResourceId.length > 0) {
      const itemsToAdd: EditResourceRequest[] = nodesWithoutResourceId.map(
        (spot) => {
          const label = getSpotNameFromSpotShapeItem(spot as SpotShape);
          return {
            type: spot.spotType as ResourceType,
            parent_id: floor.id,
            name: spot.name || '',
            total_slots: 1,
            maximum_slots_per_user: 1,
            is_open: true,
            is_approvable: false,
            is_available: true,
            // Set to what is currently shared by the API since we aren't exposing
            // changing this variable yet.
            is_shareable: false,
            weight: 0,
            map_url: null,
            label,
            description: '',
            map_data: '',
            office_id: officeId
          };
        }
      );

      const result =
        await api.client.organizations.batchResourcePost(itemsToAdd);
      (result.result || []).forEach((resource, index) => {
        if (nodesWithoutResourceId[index] && resource.id) {
          data.shapes = assignResourceIdToSpotId(
            data.shapes,
            nodesWithoutResourceId[index].id,
            resource.id
          );
        }
      });
    }

    if (nodeWithResourceId.length > 0) {
      const itemsToUpdate: Partial<EditResourceRequest>[] =
        nodeWithResourceId.map((spot) => {
          const label = getSpotNameFromSpotShapeItem(spot as SpotShape);
          return {
            type: spot.spotType as ResourceType,
            ID: spot.resource_id,
            label,
            name: spot.name || '',
            parent_id: floor.id,
            office_id: officeId
          };
        });

      await api.client.organizations.batchResourcePut(
        itemsToUpdate as EditResourceRequest[]
      );
    }

    try {
      if (nodesToDelete.length > 0) {
        const itemsToDelete: string[] = nodesToDelete.map(
          (spot) => spot.resource_id || ''
        );
        await api.client.organizations.batchResourceDelete(itemsToDelete);
      }
    } catch (e) {
      logError('Failed to delete resources', e);
    }

    if (floor) {
      const request: EditResourceRequest = {
        ...floor,
        map_data: JSON.stringify(data)
      };

      await api.client.organizations.resourcePut(floor.id, request);
    }

    queryClient.invalidateQueries(['organizations.resourcesAll']);
    queryClient.invalidateQueries(['organizations.offices']);
    queryClient.invalidateQueries(['schedules']);
    queryClient.invalidateQueries(['occupied']);
    queryClient.invalidateQueries([
      'organizations.resourcesAll',
      officeId ?? '',
      ['area'],
      floor?.id ?? ''
    ]);

    onSaved?.();

    toast.success('Changes applied successfully');
  };

  return (
    <div className={'relative flex h-full w-full'}>
      <MapBuilder
        onSave={onMapSave}
        value={floorData}
        resources={officeResources}
        initialPosition={initialPosition}
        setEditingResources={setEditingResources}
        editingResources={editingResources}
        editorRef={editorRef}
      />
    </div>
  );
};
